import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";

import { useConfirmationDialogContext } from "../confirmation-dialog/ConfirmationDialogContext";
import { useDirtyFormContext } from "../forms/dirty-form/DirtyFormContext";

/**
 * Blocks in-app navigation (and the browser unload) while a form registered with DirtyFormContext
 * has unsaved changes, and asks for confirmation before leaving. Mount it once per app inside the
 * router, below DirtyFormContextProvider and ConfirmationDialogContextProvider.
 */
export const NavigationBlocker = () => {
  const { isDirty, clearAllDirtyForms } = useDirtyFormContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const { t } = useTranslation();

  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (blocker.state !== "blocked") {
      return;
    }

    showConfirmationDialog({
      dialogTitleText: t("common.formDialog.unsavedChangesTitle"),
      dialogText: t("common.formDialog.unsavedChangesText"),
      agreeText: t("common.formDialog.leavePage"),
      discardText: t("common.cancel"),
      onClose: (confirm) => {
        if (confirm) {
          clearAllDirtyForms();
          blocker.proceed();
        } else {
          blocker.reset();
        }
      },
    });
  }, [blocker, clearAllDirtyForms, showConfirmationDialog, t]);

  return null;
};
